const title = `Tips to Boost Your "Second Brain" (OneNote)`;
const date = `March 2021`;
export const productivityPost = {
  title: title,
  date: date,
  url: 'posts/productivity',
  body: `# ${title}
## Taking Notes has Compounding Effects on your Productivity

<sub>${date}</sub>

<span class="post-first-character">I</span>

find it fascinating to read the original [OneNote's announcement post](https://news.microsoft.com/2002/11/17/bill-gates-announces-microsoft-onenote-a-new-application-to-make-note-taking-more-productive/) which extoles the benefits taking digital free-form notes and organizing them in a personalized way. Features like universal search, sophisticated organization, and cross-device synchronization have made note taking even more valuable.

I believe taking notes consistently has tremendously increased my productivity. Consider what is possible in the long term. [Niklas Luhmann](https://en.wikipedia.org/wiki/Niklas_Luhmann) (1927–1998) is a sociology researcher famous for his extensive system of note taking. Luhmann built up an organization of some 90,000 index cards for his research, and credited it for enabling his extraordinarily prolific writing including over 70 books and 400 scholarly articles [1].

Here are my 7 habits for highly effective note taking.

**Table of Contents**

1. Take Notes on Everything
2. Make Your Notes Beautiful
3. Stay Organized
4. Be Quick by Using Hotkeys
5. Protect Your Notes
6. Paper Notes are Still Relevant

Note: Some features are OneNote specific.

## 1. Take notes on everything, such as:

* **Master To Do** — Have one place to track and prioritize tasks across multiple projects. I just move lines up and down to reprioritize items or projects (see below for the hotkeys I use for this).

* **Places** (ex. home, cottage, parent's house, etc.) — Address, WiFi login, home insurance, routine maintenance, and improvement ideas.

* **People** — Record memories, quotes, or ideas for the future.

* **Devices** (ex. laptop, TV, camera, vehicle, etc.) — Model, specs, price, and customizations.

* **Software tools** (ex. Python, ElasticSearch, etc.) — Common commands, errors, solutions, and links to online resources.

* **Jokes** — Favourite jokes and funny sayings. Review this note before going to a party to get your funny on.

* **Addresses** — Where you've lived or where family members live. Useful for sending gifts.

* **Birthdays and Gift Ideas** — Birthdays of family and friends. Record gift ideas even when birthdays are months away.

* **Taxes** — Record donations, investments, who you worked for, etc. This will help you file taxes at the end of the year.

* **Thoughts on *X*** — Journal thoughts on life, love, and specific interests.

* **Lessons Learned** — Miscellaneous concepts learned or mistakes made that don't fit anywhere else.

* **Motivation** — How you stay happy and productive. Re-read as needed.

* **Accomplishments** — Life milestones and company specific achievements.

## 2. Make Your Notes Beautiful

Beauty arises from the orderly arrangement of parts. Beautiful notes are easier to remember and more enjoyable to re-read.

* **Colors and layout** — A little bit goes a long way.

* **Tables for structure** (see image below, left)

* **To do lists** (see image below, right)

<div align="center" class="pub-img-background">
<img src="../images/prod-table.png" width="60%">
<img src="../images/prod-todo.png" width="25%">
</div>


* **Copy-paste styles** — Quickly re-apply beautiful styles. *(Ctrl+Shift+C / Ctrl+Shift+C)*

* **Paste with Source Formatting or Text Only** — Choose to keep style or remove it when pasting text. *(Ctrl, K or Ctrl, T)* (image below left)

* **Clear formatting** — Quickly remove text style. (image below right)

<div align="center" class="pub-img-background">
<img src="../images/prod-paste.png" width="50%">
<img src="../images/prod-clear.png" width="25%">
</div>


* **PDFs** — Export to PDF to share your beautiful notes with others.

## 3. Stay Organized

OneNote's organization can have 6 layers of hierarchy as seen below:

<div align="center" class="pub-img-background">
<img src="../images/prod-levels.png" width="90%">
</div>

* **Table of Contents** — I use a [free plugin](https://getonetastic.com/macro-01C7C254D45E462CB24B3246853B3032) for OneNote to generate Tables of Contents (image above).

* **Growing Notes** — Add important info and links at the top of the note and add other new additions at the bottom of the note. Insert date with *Ctrl+Shift+T* as you make additions or right click on any line to see when it was added.

* **Duplication** — Do not copy the same text in two different notes. Copy links to paragraphs (image below).

<div align="center" class="pub-img-background">
<img src="../images/prod-link.png" width="30%">
</div>

* **Date Added** — Right click on any text and the date that you wrote it is at the bottom of the menu (image above).

## 4. Be faster with hotkeys

Hotkeys save you time and provide special abilities. This in turn makes your work more enjoyable and leads to more polished results.

### Find Notes Faster

Here are my favourite hotkeys to find and view notes:

<div align="center" class="pub-img-background">
<img src="../images/prod-view.png" width="80%">
</div>

I tend to put multiple keywords into note titles to improve searching. For example, I might combine several titles like, "About Me / Personal Bio / Introductions" because I might forget what I originally named the note.


### Style Notes Faster

Here are my top shortcuts to apply style:

<div align="center" class="pub-img-background">
<img src="../images/prod-style.png" width="75%">
</div>


### Write Notes Faster

I highly recommend using advanced cursor movement shortcuts like jumping (or selecting) whole words as seen here:

<div align="center" class="pub-img-background">
<img src="../images/prod-cursor.gif" width="35%">
</div>

Here's my full list of cursor movement shortcuts that I use:

<div align="center" class="pub-img-background">
<img src="../images/prod-move.png" width="68%">
</div>


### Other Speed Boosts

* **Right-click without a mouse** — *Shift+F10*. I find this useful when creating macros.

* **Fast Spelling Correct** — If the word has a red underline then right-click it (or *Shift+F10)*, press down, and then enter. This will choose the first spelling suggestion in many applications.

* **Type the underscored letter to make a menu or prompt selection** — Sometimes holding alt is also needed (image below).

<div align="center" class="pub-img-background">
<img src="../images/prod-letter.png" width="92%">
</div>

## 5. Protect Your Notes

Make sure to enable **password protection** for notes containing sensitive information like passwords, personal details, and business information. These notes should be encrypted and only temporarily unlocked using the password.

Secondly, **backup your notes** by exporting them to a backup drive or a trusted secondary cloud. The wealth of information in your notes is too great to lose.

## 6. Paper Notes Are Still Relevant

Paper notes have no distractions and are better for sketching and connecting ideas. Some notes are not worth keeping in your electronic notebook like early ideation. Your immediate to do lists are more helpful on a pad of paper that never leaves your sight. Transcribing notes from paper to computer is an opportunity for refinement. Or, take a picture of a paper note and put it into your electronic notebook.

## Conclusion
Taking Notes has Compounding Effects on your Productivity
What a world we live in where we can take all our notes with us and search them instantly. I really enjoy looking back at my notes. My notes are my personal collection of guides and memories, and they provide a wealth of inspiration and expertise for me to direct into my future projects. I have been a devoted advocate of OneNote for a long time, and over the years I have introduced it to a number of friends and colleagues. It is gratifying to hear even years later, how OneNote has been a game changer for them, just as it has been for me.
<span class="post-trailing-square">■</span>

<br>

[1] Schmidt, Johannes. “Niklas Luhmann's Card Index: Thinking Tool, Communication Partner, Publication Machine”. In Cevolini, Alberto (ed.). [Forgetting Machines : Knowledge Management Evolution in Early Modern Europe](https://pub.uni-bielefeld.de/download/2942475/2942530/jschmidt_2016_niklas%20luhmanns%20card%20index.pdf). pp. 289-311.
`};
